import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import Spinner from '../../shared/spinner';
import InputMask from 'react-input-mask';
import { format, parse, subDays } from 'date-fns';
import { Empregador } from '../../shared/model/empregador';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import EmpregadorService from '../../services/empregadorService';
import CustomAlert from '../../shared/customAlert';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import RelatorioService from '../../services/relatorioService';
import PromotoraService from '../../services/promotoraService';
import { Promotora } from '../../shared/model/promotora';
import { RelatoriosPriceDTO } from '../../shared/dto/relatoriosPriceDTO';
import OriginadorService from '../../services/originadorService';
import { Originador } from '../../shared/model/originador';

// Instância dos serviços fora ou dentro do componente (mantendo seu padrão fora)
const relatorioService: RelatorioService = new RelatorioService();
const empregadorService: EmpregadorService = new EmpregadorService();
const promotoraService: PromotoraService = new PromotoraService();
const originadorService: OriginadorService = new OriginadorService();

function RelatorioFatorPriceComponent() {
  useVerificaPermissao();
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const [dtBase1, setDtBase1] = useState(format(subDays(new Date(), 15), 'dd/MM/yyyy'));
  const [dtBase2, setDtBase2] = useState(format(new Date(), 'dd/MM/yyyy'));

  const [promotora, setPromotora] = useState('');
  const [promotoras, setPromotoras] = useState<Promotora[]>([]);
  const [empregador, setEmpregador] = useState('');
  const [empregadores, setEmpregadores] = useState<Empregador[]>([]);

  const [originador, setOriginador] = useState('');
  const [originadores, setOriginadores] = useState<Originador[]>([]);

  const listarEmpregadores = async () => {
    setLoading(true);
    try {
      const response = await empregadorService.listarTodosEmpregadoresAtivos();
      setEmpregadores(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const listarOriginadores = async () => {
    setLoading(true);
    try {
      const resp = await originadorService.listarTodasOriginadoresPorRole();
      setOriginadores(resp.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const filtrarPromotoras = async () => {
        setLoading(true);
        try {
            let listaAtualizada = [];

            if (originador && originador !== '') {
                const response = await promotoraService.buscarPorOriginador([Number(originador)]);
                listaAtualizada = response.data;
            } else {
                const response = await promotoraService.listarTodasPromotorasAtivasPorRole();
                listaAtualizada = response.data;
            }

            setPromotoras(listaAtualizada);
            
            if (listaAtualizada.length > 0) {
               setPromotora(''); 
            } else {
               setPromotora('');
            }

        } catch (err) {
            console.error("Erro ao carregar promotoras", err);
            setAlert({ message: 'Erro ao carregar lista de promotoras', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    filtrarPromotoras();
  }, [originador]);
  // ----------------------------------------------------------------

  const gerarRelatorio = async () => {
    setLoading(true);
    try {
      const dto: RelatoriosPriceDTO = new RelatoriosPriceDTO();

      dto.idEmpregadores = Number(empregador);
      dto.idPromotoras = Number(promotora);
      
      if (originador) {
          (dto as any).idOriginador = Number(originador); 
      }

      dto.dtCadastroInicio = parse(dtBase1, "dd/MM/yyyy", new Date());
      dto.dtCadastroFim = parse(dtBase2, "dd/MM/yyyy", new Date());

      const response = await relatorioService.gerarRelatorioPrice(dto);

      const contentType = response.headers['content-type'];

      if (contentType === 'text/plain') {
        const errorText = await new Response(response.data).text();
        setAlert({ message: errorText, type: 'warning' });
      } else {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        link.setAttribute('download', 'Relatorio_Fatores_Price.xlsx');

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.status === 500 && err.response.data instanceof Blob) {
        const errorText = await new Response(err.response.data).text();
        setAlert({ message: errorText, type: 'warning' });
      } else if (err.response && err.response !== 'AXIOS_TIMEOUT') {
        setAlert({ message: 'Ocorreu um erro ao gerar o relatório', type: 'error' });
      }
      console.error(err);
    }
  };


  useEffect(() => {
    listarEmpregadores();
    listarOriginadores();
  }, []);


  return (
    <>
      <div className="position-relative m-md-3">
        {alert && (
          <CustomAlert
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert(null)}
          />
        )} 
        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <strong>Relatório de Fatores Price</strong>
            </div>
            <div className="card-body">
              <div className="row mb-3">

                <div className="row" style={{ marginTop: '10px' }}>
                  <div className="col-md-3">
                    <label>{Originador.DESCRITIVO_GERAL}</label>
                    <select
                      className="form-select"
                      value={originador}
                      onChange={(e) => setOriginador(e.target.value)}
                    >
                      <option value="">Todos</option>
                      {originadores.map(org => (
                        <option key={org.id ?? 'null'} value={org.id?.toString() ?? ''}>
                          {org.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row" style={{ marginTop: '10px' }}>
                  <div className="col-md-3">
                    <label>Promotora</label>
                    <select
                      className="form-select"
                      value={promotora}
                      onChange={(e) => setPromotora(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {promotoras.map(e => (
                        <option key={e.id ?? 'null'} value={e.id?.toString() ?? ''}>
                          {e.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row" style={{ marginTop: '10px' }}>
                  <div className="col-md-3">
                    <label>Empregador</label>
                    <select
                      className="form-select"
                      value={empregador}
                      onChange={(e) => setEmpregador(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {empregadores.map(e => (
                        <option key={e.id ?? 'null'} value={e.id?.toString() ?? ''}>
                          {e.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>

              <div className="row" style={{ marginTop: '10px' }}>
                <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                  <label>Período de Data <span style={{ color: 'red' }}>*</span></label>
                </div>
              </div>
              <div className="row" style={{ marginTop: '5px' }}>
                <div className="col-md-4 d-flex align-items-center">
                  <InputMask
                    required
                    mask="99/99/9999"
                    className="form-control"
                    type="text"
                    value={dtBase1}
                    onChange={(e) => setDtBase1(e.target.value.replace(/_/g, ""))}
                    style={{ width: '150px', marginRight: '10px' }}
                  />
                  <span style={{ marginRight: '10px' }}>a</span>
                  <InputMask
                    required
                    mask="99/99/9999"
                    className="form-control"
                    type="text"
                    value={dtBase2}
                    onChange={(e) => setDtBase2(e.target.value.replace(/_/g, ""))}
                    style={{ width: '150px' }}
                  />
                </div>
              </div>

              <button className="btn btn-primary mt-3" onClick={gerarRelatorio}>
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Gerar Relatório
              </button>

            </div>
          </div>
        )}
      </div>
    </>
  );
}
export default RelatorioFatorPriceComponent;